import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Plus, BellRing } from "lucide-react";
import ReminderDialog from "@/components/app/ReminderDialog";
import { formatDateTimeIt } from "@/lib/format";

const MESI = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];
const GIORNI = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function startOfMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const dayOfWeek = (first.getDay() + 6) % 7; // Monday=0
  first.setDate(first.getDate() - dayOfWeek);
  first.setHours(0, 0, 0, 0);
  return first;
}

export default function Calendario() {
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [reminders, setReminders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [prefillDate, setPrefillDate] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const r = await api.get("/reminders", { params: { filter: "all" } });
    setReminders(r.data);
  }, []);
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("crm:data-changed", h);
    return () => window.removeEventListener("crm:data-changed", h);
  }, [load]);

  const grid = useMemo(() => {
    const start = startOfMonthGrid(cursor.y, cursor.m);
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(d);
    }
    return days;
  }, [cursor]);

  const byDate = useMemo(() => {
    const map = new Map();
    reminders.forEach((r) => {
      const d = new Date(r.scheduled_at);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return map;
  }, [reminders]);

  const keyOf = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  const isToday = (d) => {
    const n = new Date();
    return d.getDate() === n.getDate() && d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();
  };
  const isCurrentMonth = (d) => d.getMonth() === cursor.m;

  const goPrev = () => setCursor((c) => {
    const m = c.m - 1;
    return m < 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m };
  });
  const goNext = () => setCursor((c) => {
    const m = c.m + 1;
    return m > 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m };
  });
  const goToday = () => setCursor({ y: now.getFullYear(), m: now.getMonth() });

  const selectedItems = selected ? (byDate.get(keyOf(selected)) || []) : [];

  const openNew = (d) => {
    setEditing(null);
    if (d) {
      const dt = new Date(d);
      dt.setHours(9, 0, 0, 0);
      setPrefillDate(dt.toISOString());
    } else {
      setPrefillDate(null);
    }
    setDialogOpen(true);
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1600px]">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.16em] text-[#F56A1F] font-semibold mb-2">Vista Mensile</div>
          <h1 className="text-4xl font-semibold tracking-tight text-stone-900">Calendario</h1>
          <p className="text-stone-500 mt-1">Tutti i richiami visualizzati sul calendario.</p>
        </div>
        <Button
          onClick={() => openNew()}
          className="bg-[#F56A1F] hover:bg-[#E05206] text-white gap-2 shadow-sm">
          <Plus className="w-4 h-4" /> Nuovo Richiamo
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5">
        {/* Calendar */}
        <Card className="border-stone-200 shadow-sm rounded-2xl overflow-hidden">
          <div className="grain-orange bg-[#F56A1F] px-5 py-3 flex items-center justify-between">
            <div className="text-white font-medium">{MESI[cursor.m]} {cursor.y}</div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={goPrev} className="text-white/90 hover:text-white hover:bg-white/10 h-8 w-8 p-0">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={goToday}
                className="text-white/90 hover:text-white hover:bg-white/10 h-8 text-xs">
                Oggi
              </Button>
              <Button size="sm" variant="ghost" onClick={goNext} className="text-white/90 hover:text-white hover:bg-white/10 h-8 w-8 p-0">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-7 border-b border-stone-200 bg-stone-50/50">
            {GIORNI.map((g) => (
              <div key={g} className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold text-center py-2">
                {g}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {grid.map((d) => {
              const items = byDate.get(keyOf(d)) || [];
              const active = selected && keyOf(selected) === keyOf(d);
              return (
                <button
                  key={keyOf(d)}
                  onClick={() => setSelected(d)}
                  onDoubleClick={() => openNew(d)}
                  className={`h-24 border-b border-r border-stone-100 p-2 text-left transition-colors relative ${
                    isCurrentMonth(d) ? "bg-white hover:bg-stone-50/70" : "bg-stone-50/40 text-stone-400"
                  } ${active ? "ring-2 ring-inset ring-[#F56A1F]" : ""}`}
                >
                  <div className={`text-sm font-medium ${isToday(d) ? "text-white" : ""}`}>
                    <span className={isToday(d) ? "inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#F56A1F]" : ""}>
                      {d.getDate()}
                    </span>
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {items.slice(0, 2).map((r) => (
                      <div
                        key={r.id}
                        className={`text-[10.5px] px-1.5 py-0.5 rounded truncate ${
                          r.completed ? "line-through opacity-60 bg-stone-100 text-stone-500" :
                          r.priorita === "alta" ? "bg-red-100 text-red-800" :
                          r.priorita === "bassa" ? "bg-stone-100 text-stone-700" :
                          "bg-[#F56A1F]/10 text-[#F56A1F]"
                        }`}
                      >
                        {r.titolo}
                      </div>
                    ))}
                    {items.length > 2 && (
                      <div className="text-[10px] text-stone-400">+{items.length - 2} altri</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Side panel */}
        <Card className="border-stone-200 shadow-sm rounded-2xl p-5 h-fit">
          <div className="text-xs uppercase tracking-widest text-[#F56A1F] font-semibold mb-1">
            {selected ? "Giorno Selezionato" : "Suggerimento"}
          </div>
          <div className="text-lg font-semibold text-stone-900 mb-4">
            {selected
              ? selected.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
              : "Seleziona un giorno"}
          </div>
          {!selected ? (
            <p className="text-sm text-stone-500 leading-relaxed">
              Clicca su un giorno per vedere i richiami. Doppio clic per crearne uno nuovo su quella data.
            </p>
          ) : selectedItems.length === 0 ? (
            <div>
              <p className="text-sm text-stone-500 mb-3">Nessun richiamo in questo giorno.</p>
              <Button onClick={() => openNew(selected)} variant="outline" className="w-full gap-2">
                <Plus className="w-4 h-4" /> Crea richiamo
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {selectedItems.map((r) => (
                <div
                  key={r.id}
                  onClick={() => { setEditing(r); setDialogOpen(true); }}
                  className="p-3 rounded-lg border border-stone-100 hover:border-stone-200 hover:bg-stone-50/60 cursor-pointer transition-colors">
                  <div className="flex items-start gap-2">
                    <BellRing className="w-4 h-4 text-[#F56A1F] mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium text-sm ${r.completed ? "line-through text-stone-400" : "text-stone-900"}`}>
                        {r.titolo}
                      </div>
                      {r.client_ragione_sociale && (
                        <div
                          onClick={(e) => { e.stopPropagation(); navigate(`/clienti/${r.client_id}`); }}
                          className="text-xs text-[#F56A1F] mt-0.5 hover:underline">
                          {r.client_ragione_sociale}
                        </div>
                      )}
                      <div className="text-[11px] text-stone-400 font-mono mt-1">{formatDateTimeIt(r.scheduled_at)}</div>
                    </div>
                  </div>
                </div>
              ))}
              <Button onClick={() => openNew(selected)} variant="outline" className="w-full gap-2 mt-2">
                <Plus className="w-4 h-4" /> Aggiungi
              </Button>
            </div>
          )}
        </Card>
      </div>

      <ReminderDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) { setEditing(null); setPrefillDate(null); } }}
        initial={editing || (prefillDate ? { scheduled_at: prefillDate, titolo: "", descrizione: "", priorita: "media" } : null)}
        onSaved={load}
      />
    </div>
  );
}
